import React, { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import styles from "./TeamForm.module.css";
import Spinner from "../Spinner/Spinner";
import Popup from "../Popup/Popup";
import { API } from "../../config";
import { useImageUpload } from "./hooks/useImageUpload";

function TeamUpdate() {
  const [name, setName] = useState("");
  const [position, setPosition] = useState("");
  const [description, setDescription] = useState("");
  const [twitter, setTwitter] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [mail, setMail] = useState("");
  const [existingImage, setExistingImage] = useState("");
  const [spin, setSpin] = useState(true);
  const [saving, setSaving] = useState(false);
  const [show, setShow] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const fileInputRef = useRef(null);
  const params = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (!show) return;
    const timer = setTimeout(() => {
      setShow(false);
      navigate("/team");
    }, 2000);
    return () => clearTimeout(timer);
  }, [show]);

  const {
    selectedFile,
    imagePreview,
    uploadError,
    setUploadError,
    handleFileChange,
    uploadImage,
    resetImageState,
  } = useImageUpload();

  const getData = async () => {
    const res = await fetch(`${API}/Admin/team/${params.id}`);
    const data = await res.json();
    const member = data.member || data;
    setName(member.name || "");
    setPosition(member.position || "");
    setDescription(member.description || "");
    setTwitter(member.twitter || "");
    setLinkedin(member.linkedin || "");
    setMail(member.mail || "");
    setExistingImage(member.image || "");
    setSpin(false);
  };

  useEffect(() => {
    getData();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      let imageUrl = existingImage;

      if (selectedFile) {
        imageUrl = await uploadImage(selectedFile);
      }

      const payload = {
        name,
        position,
        image: imageUrl,
        description,
        twitter,
        linkedin,
        mail,
      };

      await axios.put(`${API}/Admin/team/${params.id}`, payload);

      setShow(true);
      setSaving(false);
      setExistingImage(imageUrl);
      resetImageState();
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      console.error(err);
      setUploadError("Failed to update. Please try again.");
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    await axios.delete(`${API}/Admin/team/${params.id}`);
    navigate("/team");
  };

  const handleDeleteClick = () => setShowConfirm(true);

  const displayImage = imagePreview || existingImage;

  return (
    <>
      {(spin || saving) && <Spinner />}
      {show && (
        <Popup show={show} switch={() => setShow(false)} message="Member updated successfully" />
      )}
      {showConfirm && (
        <div className={styles.confirmOverlay}>
          <div className={styles.confirmBox}>
            <p className={styles.confirmText}>
              Are you sure you want to delete <strong>{name}</strong>? This is an irreversible action.
            </p>
            <div className={styles.confirmActions}>
              <button className={styles.confirmBttnDelete} onClick={handleDelete}>Yes, Delete</button>
              <button className={styles.confirmBttn} onClick={() => setShowConfirm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {!spin && (
        <div className={styles.container}>
          <div className={styles.inner}>
            <div className={styles.upper}>
              <Link to="/">
                <span className={styles.swag}>
                  Website <span className={styles.greater}>&gt;</span>
                </span>
              </Link>
              <Link to="/team">
                <span className={styles.swag}>
                  Team <span className={styles.greater}>&gt;</span>
                </span>
              </Link>
              <span className={styles.req}>Edit Member</span>
            </div>

            <h1 className={styles.pageTitle}>Edit Team Member</h1>

            <form onSubmit={handleSubmit}>
              <div className={styles.items}>
                <div className={styles.field}>
                  <p>Name *</p>
                  <input
                    id={styles.gap}
                    placeholder="Full Name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>

                <div className={styles.field}>
                  <p>Position *</p>
                  <input
                    id={styles.gap}
                    placeholder="e.g. Designer"
                    type="text"
                    value={position}
                    onChange={(e) => setPosition(e.target.value)}
                    required
                  />
                </div>

                <div className={styles.field}>
                  <p>Photo *</p>
                  <div
                    className={styles.imageUploadDiv}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {displayImage ? (
                      <img src={displayImage} alt="Preview" className={styles.uploadedImage} />
                    ) : (
                      <span className={styles.uploadPlaceholder}>Click to upload photo</span>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,image/svg+xml"
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                  />
                  {imagePreview && (
                    <p className={styles.imageNote}>New image selected — will upload on save</p>
                  )}
                  {uploadError && <p className={styles.error}>{uploadError}</p>}
                </div>

                <div className={styles.field}>
                  <p>Bio *</p>
                  <textarea
                    id={styles.gapdesc}
                    placeholder="Short bio"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    required
                  />
                </div>

                <div className={styles.field}>
                  <p>Twitter (optional)</p>
                  <input
                    id={styles.gap}
                    placeholder="https://twitter.com/..."
                    type="url"
                    value={twitter}
                    onChange={(e) => setTwitter(e.target.value)}
                  />
                </div>

                <div className={styles.field}>
                  <p>LinkedIn (optional)</p>
                  <input
                    id={styles.gap}
                    placeholder="https://linkedin.com/in/..."
                    type="url"
                    value={linkedin}
                    onChange={(e) => setLinkedin(e.target.value)}
                  />
                </div>

                <div className={styles.field}>
                  <p>Email (optional)</p>
                  <input
                    id={styles.gap}
                    placeholder="john@example.com"
                    type="email"
                    value={mail}
                    onChange={(e) => setMail(e.target.value)}
                  />
                </div>

                <div className={styles.buttonRow}>
                  <input type="submit" className={styles.bttn} value="Update" />
                  <button type="button" className={styles.bttnDelete} onClick={handleDeleteClick}>
                    Delete
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default TeamUpdate;
